import type { SetVariantProductVariantInput, VariantProductVariant } from '~/types/graphql';
import type { Variant } from '../types';

import { cleanDecimal } from '~/utils/number';

type IProductVariant = VariantProductVariant & { baseID: string };

/** Entries without a baseID are dropped — nothing can address them. */
export const toProductVariantByBaseID = (
  productVariants: Variant['productVariants'],
): Map<string, IProductVariant> =>
  new Map(
    (productVariants ?? [])
      .filter((productVariant): productVariant is IProductVariant => !!productVariant?.baseID)
      .map((productVariant) => [productVariant.baseID, productVariant]),
  );

/**
 * One amount as it is sent. The server answers `10.00` where a merchant types `10`, and a half-typed
 * `10.` is still a number, so both are normalised before they are compared or sent.
 */
export const toProductVariantAmount = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';

  const amount = Number(value);

  return Number.isFinite(amount) ? cleanDecimal(amount) : '';
};

/**
 * Compared by numeric value rather than by the rounded string: `23.11234` against a saved `23.11` is
 * still an edit the merchant can see on screen, while `10` against `10.00` is the same number.
 */
interface IIsProductVariantAmountDifferentParams {
  amount: unknown;
  storedAmount: unknown;
}

const isProductVariantAmountDifferent = ({
  amount,
  storedAmount,
}: IIsProductVariantAmountDifferentParams): boolean => {
  const value = toProductVariantAmount(amount);
  const storedValue = toProductVariantAmount(storedAmount);
  if (!value || !storedValue) return value !== storedValue;

  return Number(amount) !== Number(storedAmount);
};

/**
 * Whether a price-test Variant's per-variant prices differ from what is saved. Matched by baseID, so
 * order and record ids don't count, and a blank entry reads the same as a missing one.
 */
export interface IIsVariantProductVariantsDifferentParams {
  productVariants: Variant['productVariants'];
  storedProductVariants: Variant['productVariants'];
}

export const isVariantProductVariantsDifferent = ({
  productVariants,
  storedProductVariants,
}: IIsVariantProductVariantsDifferentParams): boolean => {
  const current = toProductVariantByBaseID(productVariants);
  const stored = toProductVariantByBaseID(storedProductVariants);
  const baseIDs = new Set([...current.keys(), ...stored.keys()]);

  return [...baseIDs].some((baseID) => {
    const productVariant = current.get(baseID);
    const storedProductVariant = stored.get(baseID);

    return (
      isProductVariantAmountDifferent({
        amount: productVariant?.price,
        storedAmount: storedProductVariant?.price,
      }) ||
      isProductVariantAmountDifferent({
        amount: productVariant?.compareAtPrice,
        storedAmount: storedProductVariant?.compareAtPrice,
      })
    );
  });
};

/**
 * The per-variant prices as `variantProductVariantsBulkSet` takes them. Only what is filled in is
 * sent: a variant left out charges what the Control's product does, which is also how a field cleared
 * back to blank drops its override instead of being sent as zero.
 */
export const toSetVariantProductVariantInput = (
  productVariants: Variant['productVariants'],
): SetVariantProductVariantInput[] =>
  [...toProductVariantByBaseID(productVariants).values()].flatMap((productVariant) => {
    const price = toProductVariantAmount(productVariant.price);
    const compareAtPrice = toProductVariantAmount(productVariant.compareAtPrice);
    if (!price && !compareAtPrice) return [];

    return [
      {
        baseID: productVariant.baseID,
        ...(price ? { price } : {}),
        ...(compareAtPrice ? { compareAtPrice } : {}),
      },
    ];
  });
